import {inject, Injectable} from '@angular/core';
import {Observable} from 'rxjs';
import {
  BasicUserDetailsListRestResponse,
  CommentService,
  TaCommentDetailsListRestResponse,
  TaCommentDetailsRestResponse,
} from '../api';

/**
 * The TA comment chain: read, post, edit, remove.
 *
 * Not queued. QueueService serialises the field / traveller / itinerary writes that all mutate the
 * same TA record; a comment is its own resource, so putting it on the queue would only make it wait
 * behind an unrelated save. Nothing here reads or writes store.taSubmission() either, so a comment
 * in flight cannot race one.
 *
 * Every call returns the comment the server stored, which is what the chain replaces its copy with -
 * so the edit and delete stamps on screen are the server's own, not a guess made here.
 */
@Injectable({
  providedIn: 'root'
})
export class TaCommentsDatasource {
  private commentApi = inject(CommentService);

  /**
   * The whole chain for a TA. Callers re-request on every open rather than caching: a chain is
   * small, and a stale one is the whole point of the feature going missing.
   */
  loadTaComments(taId: string): Observable<TaCommentDetailsListRestResponse> {
    return this.commentApi.getSubmissionComments(taId);
  }

  /**
   * Who can be @mentioned in this TA's chain. Scoped to the TA by the server - the people with a
   * part in it, not the whole directory.
   *
   * Deliberately not cached. The mentionable set is derived from who can view the TA, which is
   * mostly its travellers, and that list is edited on the very screens the chain now appears on -
   * so a list held from an earlier showing would quietly omit someone just added, and a mention
   * that cannot be picked from the list never becomes a token and never notifies anyone. The
   * caller re-requests each time the chain is shown; see TaCommentsComponent.
   */
  loadMentionList(taId: string): Observable<BasicUserDetailsListRestResponse> {
    return this.commentApi.getSubmissionCommentMentionList(taId);
  }

  /** Posts a new comment as the signed-in user and returns the comment the server stored. */
  addTaComment(taId: string, text: string): Observable<TaCommentDetailsRestResponse> {
    return this.commentApi.addSubmissionComment(taId, {text});
  }

  /** Rewrites the text of one of the caller's own comments. */
  editTaComment(taId: string, commentId: string, text: string): Observable<TaCommentDetailsRestResponse> {
    return this.commentApi.editSubmissionComment(taId, commentId, {text});
  }

  /** Removes one of the caller's own comments. The comment stays in the chain as deleted.*/
  removeTaComment(taId: string, commentId: string): Observable<TaCommentDetailsRestResponse> {
    return this.commentApi.deleteSubmissionComment(taId, commentId);
  }
}
